import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, ImagePlus, Loader2, Save, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * UploadBill — Item 7's OCR-first "Upload Service Bill" flow.
 * Camera → OCR → Review & Confirm → save to /service-bills.
 */
export default function UploadBill() {
  const nav = useNavigate();
  const { user } = useAuth();
  const cam = useRef(null);
  const gal = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    vendor: "",
    invoice_no: "",
    billed_at: new Date().toISOString().slice(0, 10),
    odometer: "",
    subtotal: "", tax: "", total: "",
    labour_total: "", parts_total: "",
    kind: "service", // service | repair
    items: [],
    notes: "",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const assigned = user?.assigned_vehicle_ids || [];
      const all = r.data || [];
      const list = (user?.is_admin || assigned.length === 0)
        ? all
        : all.filter((v) => assigned.includes(v.id));
      setVehicles(list);
    });
  }, [user]);

  const onPick = (f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    doScan(f);
  };

  const doScan = async (f) => {
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("image", f);
      const { data } = await api.post("/service-bills/scan", fd);
      const p = data.parsed || {};
      setParsed(p);
      setForm((prev) => {
        // Try to match vehicle by number
        let vid = prev.vehicle_id;
        if (p.vehicle_number && !vid) {
          const v = vehicles.find((x) => x.number.toUpperCase() === String(p.vehicle_number).toUpperCase());
          if (v) vid = v.id;
        }
        return {
          ...prev,
          vehicle_id: vid,
          vendor: p.vendor_name || prev.vendor,
          invoice_no: p.invoice_number || prev.invoice_no,
          billed_at: p.billed_at || prev.billed_at,
          odometer: p.odometer_km ? String(p.odometer_km) : prev.odometer,
          subtotal: p.subtotal != null ? String(p.subtotal) : prev.subtotal,
          tax: p.tax != null ? String(p.tax) : prev.tax,
          total: p.total != null ? String(p.total) : prev.total,
          labour_total: p.labour_total != null ? String(p.labour_total) : prev.labour_total,
          parts_total: p.parts_total != null ? String(p.parts_total) : prev.parts_total,
          items: Array.isArray(p.line_items) ? p.line_items.map((li) => ({
            description: li.description || "",
            quantity: li.quantity ?? 1,
            unit_price: li.unit_price ?? 0,
            total: li.total ?? 0,
          })) : prev.items,
          notes: p.notes || prev.notes,
        };
      });
      toast.success("Details extracted — review & save");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScanning(false);
    }
  };

  const updateItem = (i, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select the vehicle this bill belongs to");
    if (!form.total) return toast.error("Enter the total amount");
    setSaving(true);
    try {
      // Existing /service-bills accepts JSON body — items[{description,cost}]
      const items = (form.items || []).map((it) => ({
        description: it.description || "",
        cost: Number(it.total ?? it.unit_price ?? 0) || 0,
      }));
      const { data: bill } = await api.post("/service-bills", {
        vehicle_id: form.vehicle_id,
        date: String(form.billed_at).slice(0, 10),
        total_amount: parseFloat(form.total),
        vendor: form.vendor || null,
        remarks: [form.notes, form.invoice_no ? `Invoice ${form.invoice_no}` : null].filter(Boolean).join(" · ") || null,
        items,
      });
      // Attach the receipt image if we have one
      if (bill?.id && file) {
        const fd = new FormData();
        fd.append("receipt", file);
        try { await api.post(`/service-bills/${bill.id}/receipt`, fd); } catch { /* non-fatal */ }
      }
      toast.success("Bill saved to vehicle timeline");
      nav("/", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6" data-testid="upload-bill-page">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => nav(-1)} className="w-9 h-9 border border-[var(--border-line)] hover:bg-[var(--hover)] flex items-center justify-center rounded-lg" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="ft-overline text-xs">SCAN &amp; SAVE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Upload service bill</h1>
        </div>
      </div>

      {/* Step 1: camera */}
      {!preview && (
        <div className="rounded-xl border border-dashed border-[var(--border-line)] bg-[var(--bg-surface)] p-6 text-center" data-testid="upload-bill-picker">
          <input ref={cam} type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
          <input ref={gal} type="file" accept="image/*,application/pdf" hidden onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
          <p className="text-sm text-[var(--text-muted)]">Snap the workshop invoice — Gemini AI will extract vendor, items and total. You can edit anything on the review screen.</p>
          <div className="mt-4 flex gap-3 justify-center">
            <button type="button" onClick={() => cam.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF5A1F] text-white text-sm font-semibold" data-testid="upload-bill-camera-btn">
              <Camera size={16} /> Take photo
            </button>
            <button type="button" onClick={() => gal.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-line)] text-[var(--text-primary)] text-sm font-semibold" data-testid="upload-bill-gallery-btn">
              <ImagePlus size={16} /> Choose file
            </button>
          </div>
        </div>
      )}

      {/* Step 2: review */}
      {preview && (
        <>
          <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-4 flex items-start gap-4">
            <img src={preview} alt="Bill" className="w-24 h-24 object-cover rounded-lg" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#FF5A1F] inline-flex items-center gap-1">
                {scanning ? <><Loader2 size={12} className="animate-spin" /> Extracting…</> : <><Sparkles size={12} /> Extracted — review below</>}
              </div>
              <p className="mt-1 text-xs text-[var(--text-muted)]">All AI-suggested fields are editable. Your saved copy is what we keep.</p>
              <button type="button" onClick={() => { setPreview(null); setParsed(null); setFile(null); }} className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-white">
                <X size={12} /> Replace image
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kind">
                <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="upload-bill-kind">
                  <option value="service">Service</option>
                  <option value="repair">Repair</option>
                </select>
              </Field>
              <Field label="Vehicle" required>
                <select className="input" required value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="upload-bill-vehicle">
                  <option value="">Select vehicle</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number} · {v.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vendor / Workshop">
                <input className="input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="upload-bill-vendor" />
              </Field>
              <Field label="Invoice #">
                <input className="input" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
              </Field>
              <Field label="Billed on">
                <input type="date" className="input" value={String(form.billed_at).slice(0,10)} onChange={(e) => setForm({ ...form, billed_at: e.target.value })} />
              </Field>
              <Field label="Odometer (km)">
                <input type="number" className="input" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} />
              </Field>
            </div>
          </div>

          {form.items.length > 0 && (
            <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5">
              <div className="ft-overline mb-2">LINE ITEMS</div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input className="input col-span-6" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" />
                    <input type="number" className="input col-span-2" value={it.quantity} onChange={(e) => updateItem(i, { quantity: parseFloat(e.target.value) || 0 })} placeholder="Qty" />
                    <input type="number" className="input col-span-2" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} placeholder="Unit ₹" />
                    <input type="number" className="input col-span-2" value={it.total} onChange={(e) => updateItem(i, { total: parseFloat(e.target.value) || 0 })} placeholder="Total ₹" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5">
            <div className="ft-overline mb-2">TOTALS</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Labour ₹"><input type="number" className="input" value={form.labour_total} onChange={(e) => setForm({ ...form, labour_total: e.target.value })} /></Field>
              <Field label="Parts ₹"><input type="number" className="input" value={form.parts_total} onChange={(e) => setForm({ ...form, parts_total: e.target.value })} /></Field>
              <Field label="Tax ₹"><input type="number" className="input" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} /></Field>
              <Field label="Total ₹" required><input type="number" required className="input" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="upload-bill-total" /></Field>
            </div>
          </div>

          <button type="submit" disabled={saving} className="w-full bg-[#FF5A1F] hover:bg-[#E64A0F] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2" data-testid="upload-bill-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save bill to timeline
          </button>
        </>
      )}
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="ft-overline block mb-1">{label}{required && <span className="text-[#FF5A1F]"> *</span>}</span>
      {children}
    </label>
  );
}
